(() => {
  const EVT_PREFIX = 'hira-product-form:';
  const SUCCESS_MS = 2000;
  const BODY_CLASS = 'has-sticky-atc';
  // The bar replaces the hero's own ATC, so the reveal is gated on THAT control
  // leaving the viewport — not on the hero section, which is ~3 viewports tall
  // and would leave the shopper with no buy control for most of them. The
  // section is kept only as a fallback for a hero rendered without the block.
  const HERO_ATC_SELECTORS = ['.hira-hero__price-atc', '.product-hero'];
  const FOOTER_SELECTOR = '.footer-sections';

  // Entity decoding, NOT currency formatting: the shop's money format may
  // contain HTML entities for its currency symbol. Liquid renders those as
  // glyphs in the SSR paint while the JSON payload carries them literally, so
  // decoding once here is what makes both strings identical. Every value this
  // file writes is pre-formatted by Liquid — no currency maths happens here.
  let decoder = null;
  const decode = (value) => {
    if (value == null) return '';

    decoder ??= document.createElement('textarea');
    // Safe: a textarea's content is parsed as text and never executed.
    decoder.innerHTML = String(value);
    return decoder.value;
  };

  class HiraStickyAtc extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-hira-price]');
      this.compareEl = this.querySelector('[data-hira-compare]');
      this.discountEl = this.querySelector('[data-hira-discount]');
      this.savingsEl = this.querySelector('[data-hira-savings]');
      this.pillPriceEl = this.querySelector('[data-hira-pill-price]');
      this.stepper = this.querySelector('[data-hira-stepper]');
      this.qtyDisplay = this.querySelector('[data-hira-qty-display]');
      this.qtySr = this.querySelector('[data-hira-qty-sr]');
      this.decrementBtn = this.querySelector('[data-hira-qty-step="-1"]');
      this.incrementBtn = this.querySelector('[data-hira-qty-step="1"]');
      this.statusEl = this.querySelector('[data-hira-atc-status]');
      // Two ATC controls are server-rendered — one per breakpoint layout — and
      // only one is ever visible, so both are kept in lockstep rather than
      // resolved per media query.
      this.atcButtons = [...this.querySelectorAll('[data-hira-sticky-atc]')];
      this.atcLabels = [...this.querySelectorAll('[data-hira-atc-label]')];
      this.successTimer = null;
      this.heroObserver = null;
      this.footerObserver = null;
      this.loading = false;

      // The bar is a SIBLING of the hero section, not a descendant, so the
      // controller's bubbled events never reach it — `closest()` would return
      // null and every listener would be dead. The host is resolved from the
      // document and every call into it is optional-chained so a page without
      // the controller leaves the bar inert instead of throwing.
      this.host = document.querySelector('hira-product-form');

      const { saveTemplate, taxNote, atcLabel, soldOutLabel, successMessage, errorMessage } =
        this.dataset;
      this.saveTemplate = saveTemplate ?? '';
      this.taxNote = taxNote ?? '';
      this.atcLabel = atcLabel ?? '';
      this.soldOutLabel = soldOutLabel ?? '';
      this.successMessage = successMessage ?? '';
      this.errorMessage = errorMessage ?? '';

      this.max = Number(this.stepper?.dataset.hiraQtyMax) || Infinity;
      this.available = !this.classList.contains('is-sold-out');

      this.onClick = this.onClick.bind(this);
      this.onHeroIntersect = this.onHeroIntersect.bind(this);
      this.onFooterIntersect = this.onFooterIntersect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onQtyChanged = this.onQtyChanged.bind(this);
      this.onAddToCart = this.onAddToCart.bind(this);
      this.onAddSuccess = this.onAddSuccess.bind(this);
      this.onAddError = this.onAddError.bind(this);

      this.addEventListener('click', this.onClick);
      this.host?.addEventListener(`${EVT_PREFIX}variant-changed`, this.onVariantChanged);
      this.host?.addEventListener(`${EVT_PREFIX}qty-changed`, this.onQtyChanged);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart`, this.onAddToCart);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart-success`, this.onAddSuccess);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart-error`, this.onAddError);

      // No bottom padding is reserved on <body>. The bar hides as soon as the
      // footer enters the viewport, so it never overlaps the footer and there is
      // nothing to reserve. Reserving would mean maintaining a constant equal to
      // the bar's rendered height — a second source of truth that silently
      // desynchronises the moment the bar's padding or content changes.
      document.body.classList.add(BODY_CLASS);

      document.addEventListener('shopify:section:select', this.onSectionSelect);
      this.setupReveal();
      this.setupFooterGate();

      // The controller owns the quantity, so read it rather than assuming 1 — a
      // theme-editor re-render of this section alone must not drift from state.
      this.renderQuantity(this.host?.getState?.().quantity ?? 1);
    }

    disconnectedCallback() {
      clearTimeout(this.successTimer);
      this.successTimer = null;
      this.removeEventListener('click', this.onClick);
      this.host?.removeEventListener(`${EVT_PREFIX}variant-changed`, this.onVariantChanged);
      this.host?.removeEventListener(`${EVT_PREFIX}qty-changed`, this.onQtyChanged);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart`, this.onAddToCart);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart-success`, this.onAddSuccess);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart-error`, this.onAddError);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      this.heroObserver?.disconnect();
      this.heroObserver = null;
      this.footerObserver?.disconnect();
      this.footerObserver = null;
      document.body.classList.remove(BODY_CLASS);
    }

    // Desktop-only reveal. The class is toggled unconditionally because the
    // hidden state is declared only inside the `min-width: 768px` block — below
    // that the bar must always be visible, since the hero carries no price or
    // ATC on mobile. No breakpoint logic and no scroll listener are needed.
    setupReveal() {
      const target = HERO_ATC_SELECTORS.reduce(
        (found, selector) => found ?? document.querySelector(selector),
        null,
      );

      // Fail open. With nothing to observe — the bar dropped on a template
      // without a hero, or the hero deleted in the editor — a permanently hidden
      // bar would be a dead section with nothing to diagnose.
      if (!target || typeof IntersectionObserver !== 'function') {
        this.classList.add('is-revealed');
        return;
      }

      this.heroObserver = new IntersectionObserver(this.onHeroIntersect, { threshold: 0 });
      this.heroObserver.observe(target);
    }

    // The bar must never sit over the footer. Hiding it the moment the footer
    // enters the viewport is what makes reserved bottom padding unnecessary, on
    // both breakpoints — unlike the hero gate, this one is not desktop-only.
    setupFooterGate() {
      const footer = document.querySelector(FOOTER_SELECTOR);
      if (!footer || typeof IntersectionObserver !== 'function') return;

      this.footerObserver = new IntersectionObserver(this.onFooterIntersect, { threshold: 0 });
      this.footerObserver.observe(footer);
    }

    onFooterIntersect([entry]) {
      this.classList.toggle('is-at-footer', entry.isIntersecting);
    }

    onHeroIntersect([entry]) {
      // Direction matters: the target must have scrolled UP past the viewport,
      // not merely stopped intersecting. Without the `top < 0` check, any state
      // that leaves it BELOW the viewport — an anchor jump, an editor scroll —
      // would also read as "scrolled past" and reveal the bar early.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-revealed', scrolledPast);
    }

    // Selecting the section in the theme editor scrolls nowhere, so a merchant
    // sitting at the top of the page would otherwise select it and see nothing.
    onSectionSelect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;

      this.classList.add('is-revealed');
    }

    onClick(event) {
      const stepButton = event.target.closest?.('[data-hira-qty-step]');
      if (stepButton) {
        this.stepQuantity(Number(stepButton.dataset.hiraQtyStep));
        return;
      }

      if (event.target.closest?.('[data-hira-sticky-atc]')) this.requestAdd();
    }

    stepQuantity(step) {
      if (!Number.isFinite(step)) return;
      if (typeof this.host?.setQuantity !== 'function') return;

      this.host.setQuantity(this.host.getState().quantity + step);

      // Never trust the local arithmetic: setQuantity() clamps to the tracked
      // inventory and returns silently when the clamped value is unchanged, so
      // the display is always rendered from the state it actually reached.
      this.renderQuantity(this.host.getState().quantity);
    }

    // The controller listens for this event on itself and routes it through
    // routeAddToCart(), which reads its own variant and quantity. No detail is
    // sent because handleAddRequest() ignores it entirely. window.cartx() is
    // never called from here — cartx.js sniffs the call stack and rejects
    // anonymous frames.
    requestAdd() {
      if (this.loading || !this.available) return;

      this.host?.dispatchEvent(new CustomEvent(`${EVT_PREFIX}add-to-cart`, { bubbles: true }));
    }

    onQtyChanged(event) {
      this.renderQuantity(event.detail?.quantity ?? 1);
    }

    renderQuantity(value) {
      if (this.qtyDisplay) this.qtyDisplay.textContent = String(value).padStart(2, '0');
      if (this.qtySr) this.qtySr.textContent = String(value);
      if (this.decrementBtn) this.decrementBtn.disabled = value <= 1;
      if (this.incrementBtn) this.incrementBtn.disabled = value >= this.max;
    }

    onVariantChanged(event) {
      const { variant, quantity, available } = event.detail ?? {};
      if (!variant) return;

      const priceFormatted = decode(variant.price_formatted);
      if (this.priceEl) this.priceEl.textContent = priceFormatted;
      if (this.pillPriceEl) this.pillPriceEl.textContent = priceFormatted;

      const hasCompare = variant.compare_at_formatted != null;

      if (this.compareEl) {
        this.compareEl.textContent = decode(variant.compare_at_formatted);
        this.compareEl.hidden = !hasCompare;
      }

      if (this.savingsEl) {
        this.savingsEl.textContent = this.renderSaveLine(variant.savings_formatted);
        this.savingsEl.hidden = !hasCompare;
      }

      if (this.discountEl) {
        this.discountEl.textContent = `${variant.discount_percent}% off`;
        this.discountEl.hidden = !hasCompare || variant.discount_percent === 0;
      }

      // Untracked inventory reports `inventory_quantity: 0`, so a cap is only
      // real when the count is positive and the policy is not `continue`.
      this.max =
        variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0
          ? variant.inventory_quantity
          : Infinity;
      this.renderQuantity(quantity ?? this.host?.getState?.().quantity ?? 1);

      this.setAvailability(available);
    }

    setAvailability(available) {
      this.available = available;
      this.classList.toggle('is-sold-out', !available);

      for (const button of this.atcButtons) button.disabled = !available;
      for (const label of this.atcLabels) {
        label.textContent = available ? this.atcLabel : this.soldOutLabel;
      }
    }

    onAddToCart() {
      clearTimeout(this.successTimer);
      this.successTimer = null;
      this.loading = true;
      this.setStatus('');
      this.classList.add('is-loading');
      for (const button of this.atcButtons) button.disabled = true;
    }

    onAddSuccess() {
      this.clearLoading();
      this.setStatus(this.successMessage);

      this.successTimer = setTimeout(() => {
        this.setStatus('');
        this.successTimer = null;
      }, SUCCESS_MS);

      // Deliberately does NOT dispatch `cart:updated` — the controller's
      // handleCartMutated() already did, and a second dispatch would
      // double-bump the header cart count. The drawer is left alone too:
      // CartX auto-opens it on ADD.
    }

    onAddError() {
      this.clearLoading();
      this.setStatus(this.errorMessage, true);
    }

    clearLoading() {
      this.loading = false;
      this.classList.remove('is-loading');
      for (const button of this.atcButtons) button.disabled = !this.available;
    }

    setStatus(message, isError = false) {
      if (!this.statusEl) return;

      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('is-error', isError);
    }

    // `[amount]` and `[tax_note]` are literal placeholders inside a theme
    // setting. Square brackets rather than `{{ }}` because the theme editor
    // reads double braces in a text setting as a dynamic-source binding.
    renderSaveLine(amountFormatted) {
      return this.saveTemplate
        .replace('[amount]', decode(amountFormatted))
        .replace('[tax_note]', this.taxNote);
    }
  }

  if (!customElements.get('hira-sticky-atc')) {
    customElements.define('hira-sticky-atc', HiraStickyAtc);
  }
})();
